import { Button } from 'components/atoms/Button';
import { HighlightProvider } from 'hooks/useHighlight';
import { type FC } from 'react';
import type {
  NavbarItemType,
  NavbarNavigationListType,
} from 'types/navbar.types';
import AccountMenu from './AccountMenu.component';
import MoreMenu from './MoreMenu';
import NavigationItem from './NavigationItem.component';
import { PeppolNavigationItem } from './NavigationItems';
import SupportMenu from './SupportMenu';

import AldoPricingImg from 'assets/svg/aldo_pricing.svg';
import { Title } from 'components/atoms/Title';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import { useSubscriptionPlan } from 'hooks/Subscriptions';
import useSubscriptionPlanInfo from 'hooks/Subscriptions/useSubscriptionPlanInfo';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { SubscriptionPlanNameEnum } from 'types/users.types';
import { AccountablePawLogo } from 'utils/icons/figmaIcons';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';
import './bg-snow.scss';

const Sidebar: FC<{
  navigationList: NavbarNavigationListType;
  sidebarRef?: any;
  sidebarTop?: number;
}> = ({ navigationList = [], sidebarRef, sidebarTop }) => {
  const { t } = useTranslation();

  const allowedItems = navigationList.filter(
    ({ condition = true }) => !!condition,
  );

  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();

  const subscriptionPlan = useSubscriptionPlan();

  const navigate = useCustomNavigate();

  const { sidebarText } = useSubscriptionPlanInfo(
    subscriptionPlan?.planInternalName as SubscriptionPlanNameEnum,
  );

  return (
    <HighlightProvider className="animate-highlightTaxes">
      <div
        className="bottom-0 fixed z-[100] bg-transparent md:w-[var(--sidebar-width)] invisible md:visible"
        style={{ top: sidebarTop }}
        ref={sidebarRef}
      >
        <div className="bg-primary-25 border-r border-r-[#E7E7E7] h-full flex flex-col justify-between py-6 px-4 flex-grow">
          <div className="flex h-full items-center gap-2 justify-start flex-col overflow-y-auto">
            {allowedItems
              .filter(({ placement }) => placement !== 'bottom')
              .map(
                ({ Component, ...item }: NavbarItemType) =>
                  Component || <NavigationItem key={item.title} item={item} />,
              )}
            <PeppolNavigationItem />
          </div>

          <div className="flex items-center gap-2 justify-start flex-col">
            {allowedItems
              .filter(({ placement }) => placement === 'bottom')
              .map(
                ({ Component, ...item }: NavbarItemType) =>
                  Component || <NavigationItem key={item.title} item={item} />,
              )}

            {!shouldShowTaxAdvisor && <SupportMenu />}

            <MoreMenu />

            <AccountMenu />
          </div>

          {sidebarText ? (
            <div className="relative w-full group">
              <Button
                icon={AccountablePawLogo}
                structure="light"
                className="mt-2 w-full"
                color="taxes"
              >
                {t('subscription_plan_name', {
                  planName: t(subscriptionPlan?.planInternalName as string),
                })}
              </Button>

              <div className="left-[100%] w-[400px] bottom-0 bg-white mt-0.5 invisible group-hover:visible transition-opacity duration-200 absolute flex flex-col gap-2 rounded-lg overflow-hidden shadow-lg border">
                <div className="bg-taxes-300 py-4 rounded-br-xl rounded-bl-xl">
                  <div className="text-center flex items-center justify-center">
                    <img
                      src={AldoPricingImg}
                      alt="Aldo Pricing"
                      className="w-[13%]"
                    />
                  </div>

                  <div className="text-center">
                    <Title type={SizesEnum.h6} className="text-taxes-950 mt-2">
                      {t('sidebar.current_subscription_plan_widget.title', {
                        planName: t(
                          subscriptionPlan?.planInternalName as string,
                        ),
                      })}
                    </Title>
                  </div>
                </div>

                <div className="p-5">
                  <ReactMarkdown>{sidebarText as string}</ReactMarkdown>

                  <Button
                    color="taxes"
                    className="w-full mt-5"
                    onClick={() => navigate('/subscriptions')}
                  >
                    {t('sidebar.current_subscription_plan_widget.cta')}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </HighlightProvider>
  );
};

export default Sidebar;
